<script setup>
import { computed } from 'vue'

const props = defineProps({
  expendeduria: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['select'])

// Mapeo dinámico de alertas y estilos de fila
const rowClass = computed(() => {
  if (!props.expendeduria.enabled) return 'table-secondary'
  else if (props.expendeduria.has_not_installed_alert) return 'table-warning'
  else if (props.expendeduria.has_trial_ended_alert && props.expendeduria.sepa_status === 'PENDING') return 'table-danger'
  return ''
})

// Clases de Bootstrap para SEPA
const sepaBadgeClass = computed(() => {
  if (props.expendeduria.sepa_status === 'SIGNED') return 'bg-success'
  if (props.expendeduria.sepa_status === 'PENDING') return 'bg-warning text-dark'
  if (props.expendeduria.sepa_status === 'CANCELED') return 'bg-secondary'
  return 'bg-danger'

  /* return props.expendeduria.sepa_status === 'Firmado' 
    ? 'bg-success' 
    : 'bg-danger' */
})

// Clases de Bootstrap para Formación
const lessonBadgeClass = computed(() => {
  switch (props.expendeduria.lesson_status) {
    case 'COMPLETED': return 'bg-success'
    case 'SCHEDULED': return 'bg-warning text-dark'
    case 'USER_NOT_DISPOSED': return 'bg-secondary'
    default: return 'bg-danger'
  }
})

// Clases de Bootstrap para Estado de Prueba
const trialBadgeClass = computed(() => {
  switch (props.expendeduria.trial_status) {
    case 'TRIAL': return 'bg-info text-dark'
    case 'TRIAL_ENDED': return 'bg-secondary text-white'
    default: return 'bg-secondary'
  }
})

const handleClick = () => {
  emit('select', props.expendeduria.id)
}

// 1. Cálculo seguro de días desde la formación
const getDaysSinceLesson = computed(() => {
  // Si no hay fecha definida o es nula, retornamos null
  if (!props.expendeduria.lesson_date) return null
  
  // Parseo seguro de fecha evitando problemas de ISO / Timezone
  const parts = props.expendeduria.lesson_date.split('-')
  if (parts.length !== 3) return null

  const year = parseInt(parts[0], 10)
  const month = parseInt(parts[1], 10) - 1
  const day = parseInt(parts[2], 10)

  const lessonDate = new Date(year, month, day)
  const today = new Date()

  lessonDate.setHours(0, 0, 0, 0)
  today.setHours(0, 0, 0, 0)

  const diffInMs = today - lessonDate
  return Math.floor(diffInMs / (1000 * 60 * 60 * 24))
})

// 2. Alerta 20 días (Completado + Fecha válida + >= 20 días + Check desmarcado)
const alertFollowUp20Days = computed(() => {
  const days = getDaysSinceLesson.value
  return (
    props.expendeduria.lesson_status === 'COMPLETED' &&
    days !== null &&
    days >= 20 &&
    !props.expendeduria.follow_up_20_days
  )
})

// 3. Alerta 7 días (Solo activa entre los días 7 y 19)
const alertFollowUp7Days = computed(() => {
  const days = getDaysSinceLesson.value
  return (
    props.expendeduria.lesson_status === 'COMPLETED' &&
    days !== null &&
    days >= 7 &&
    days < 20 &&
    !props.expendeduria.follow_up_7_days
  )
})
</script>

<template>
  <tr 
    :class="[rowClass, 'align-middle cursor-pointer']" 
    @click="handleClick"
    title="Hacer clic para abrir ficha"
  >
    <!-- Expendeduría -->
    <td>
      <div class="fw-bold text-dark">{{ expendeduria.name }}</div>
      <small v-if="expendeduria.email" class="text-muted d-block">{{ expendeduria.email }}</small>
    </td>

    <!-- Módems (Enviados / Funcionando / Devueltos) -->
    <td>
      <span class="badge bg-light text-dark border font-monospace fs-6">
        {{ expendeduria.sent_modems }} / {{ expendeduria.working_modems }} / {{ expendeduria.returned_modems }}
      </span>
    </td>

    <!-- Periodo de Prueba -->
    <td>
      <span class="badge" :class="trialBadgeClass">
        {{ expendeduria.trial_status_label }}
      </span>
    </td>

    <!-- SEPA -->
    <td>
      <span class="badge" :class="sepaBadgeClass">
        <div v-if="expendeduria.sepa_status === 'SIGNED'">SEPA Firmado</div>
        <div v-else-if="expendeduria.sepa_status === 'PENDING'">SEPA Pendiente</div>
        <div v-else-if="expendeduria.sepa_status === 'CANCELED'">SEPA Cancelado</div>

      </span>
    </td>

    <!-- Formación -->
    <td>
      <span class="badge" :class="lessonBadgeClass">
        {{ expendeduria.lesson_status_label }}
      </span>
    </td>

    <!-- Estado & Alerta -->
    <td>
      <div v-if="!expendeduria.enabled" class="text-danger fw-bold small d-flex align-items-center gap-1">
        <span>❌ Cancelado</span>
      </div>

      <div v-else-if="expendeduria.has_not_installed_alert" class="text-warning fw-bold small d-flex align-items-center gap-1">
        <span>⚠️ >20 días sin instalar</span>
      </div>

      <div v-else-if="expendeduria.has_trial_ended_alert && expendeduria.sepa_status === 'PENDING'" class="text-danger fw-bold small d-flex align-items-center gap-1">
        <span>🚨 Pasado 1 mes de prueba (Exigir SEPA)</span>
      </div>

      <span v-else class="badge bg-success">
        ✓ En Regla
      </span>

      <!-- Alertas de Seguimiento Post-Formación (Independientes entre sí por rango) -->
      <div v-if="alertFollowUp20Days" class="text-danger fw-bold small d-flex align-items-center gap-1">
        <span>📞 >20 días sin seguimiento post-formación</span>
      </div>
      <div v-else-if="alertFollowUp7Days" class="text-warning fw-bold small d-flex align-items-center gap-1">
        <span>📞 >7 días sin seguimiento post-formación</span>
      </div>

      <div v-if="expendeduria.pending_orders > 0" class="text-info fw-bold small d-flex align-items-center gap-1">
        <span>📦 {{ expendeduria.pending_orders }} pedidos pendientes o en preparación</span>
      </div>
      <div v-if="expendeduria.in_transit_orders > 0" class="text-info fw-bold small d-flex align-items-center gap-1">
        <span>🚚 {{ expendeduria.in_transit_orders }} pedidos en tránsito o listos para enviar</span>
      </div>
    </td>
  </tr>
</template>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>